import { useEffect, useState, useRef } from "react"
import { Input } from "@components/ui/input"
import { useDebounce } from "@hooks/useDebounce"

type TTextFilterProps = {
  initialValue: string
  onChange: (value: string | undefined) => void
  placeholder?: string
  debounceMs?: number
  className?: string
}

/**
 * Text input component with optional debounce functionality
 * @param initialValue - Initial/controlled value
 * @param onChange - Callback when value changes (debounced if debounceMs > 0)
 * @param placeholder - Input placeholder text
 * @param debounceMs - Debounce delay in milliseconds (default: 0 = no debounce)
 * @param className - Additional CSS classes
 */
export function TextFilter({
  initialValue,
  onChange,
  placeholder,
  debounceMs = 0,
  className,
}: TTextFilterProps) {
  const [inputValue, setInputValue] = useState(initialValue)
  const debouncedValue = useDebounce(inputValue, debounceMs)
  const effectiveValue = debounceMs > 0 ? debouncedValue : inputValue
  const onChangeRef = useRef(onChange)
  // Keep ref updated with latest onChange to avoid stale closures in useEffect
  // This ensures we always call the most recent version without adding onChange to dependencies
  onChangeRef.current = onChange

  useEffect(() => {
    onChangeRef.current(effectiveValue || undefined)
  }, [effectiveValue])

  useEffect(() => {
    setInputValue(initialValue)
  }, [initialValue])

  return (
    <Input
      placeholder={placeholder}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      className={className}
    />
  )
}
